/**
 * Rows 2–5 of /channels/:platform (spec §3.2), or E in their place (R7).
 * - An ad channel draws four boxes:
 *   - box 2: D1's scorecards;
 *   - box 3: the platform's tiles;
 *   - box 4: D1's spend / clicks trend;
 *   - box 5: the breakdown.
 * - The GA4 tab draws one box holding its panel.
 * Every box takes the one retry (R10) and fails on its own (§5): a failed read
 * never takes a neighbour down with it.
 *
 * @module components/channels/ChannelGrid
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { OverviewBox } from '@/components/overview/OverviewBox';
import { ScorecardRow } from '@/components/overview/ScorecardRow';
import { SpendClicksChart } from '@/components/overview/SpendClicksChart';
import { rangeDays } from '@/services/looker/lookerDateRange';
import type { BoxState, OverviewWindows } from '@/services/overview/overviewTypes';
import { CHANNEL_LABEL } from '@/services/channels/channelFormat';
import type { BreakdownTab, ChannelPlatform, ChannelView, Ga4View } from '@/services/channels/channelTypes';
import { BreakdownCard } from './BreakdownCard';
import { ChannelEmptyState } from './ChannelEmptyState';
import { Ga4Panel } from './Ga4Panel';
import { PlatformTiles } from './PlatformTiles';

interface Props {
  platform: ChannelPlatform;
  view: ChannelView;
  windows: OverviewWindows;
  tab: BreakdownTab;
  onTab: (tab: BreakdownTab) => void;
  onActive: (on: boolean) => void;
  onRetry: () => void;
}

/** buildChannelView always sets ga4 on the GA4 tab. Were it ever missing, the box says it failed rather than draw nothing. */
const NO_GA4: BoxState<Ga4View> = { status: 'failed' };

export const ChannelGrid: React.FC<Props> = ({ platform, view, windows, tab, onTab, onActive, onRetry }) => {
  const { t } = useTranslation();
  const channel = CHANNEL_LABEL[platform];
  const currency = view.money?.currency ?? null;
  // E suggests "ลองขยายช่วงเป็น 28 วัน" only under 28 days (spec §5, ChannelEmptyState); it needs the period's length, not the token.
  if (view.empty !== null) return <ChannelEmptyState empty={view.empty} channelLabel={channel} windowDays={rangeDays(windows.current)} />;
  if (platform === 'analytics') {
    return (
      <OverviewBox id="ga4" title={t('channels.box.ga4')} state={view.ga4 ?? NO_GA4} onRetry={onRetry}>
        {(ga4) => <Ga4Panel view={ga4} currency={currency} />}
      </OverviewBox>
    );
  }
  return (
    <div className="space-y-4">
      <OverviewBox id="2" title={t('channels.box.scorecards', { channel })} state={view.scorecards} onRetry={onRetry}>
        {(cards) => <ScorecardRow cards={cards} currency={currency} />}
      </OverviewBox>
      <OverviewBox id="3" title={t('channels.box.tiles', { channel })} state={view.tiles} onRetry={onRetry}>
        {(tiles) => <PlatformTiles platform={platform} tiles={tiles} currency={currency} cutApplied={windows.cutApplied} />}
      </OverviewBox>
      <OverviewBox id="4" title={t('channels.box.trend')} state={view.trend} onRetry={onRetry}>
        {(trend) => <SpendClicksChart trend={trend} currency={currency} />}
      </OverviewBox>
      <BreakdownCard platform={platform} state={view.breakdown} tab={tab} onTab={onTab} onActive={onActive} currency={currency} onRetry={onRetry} />
    </div>
  );
};
